import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiGetSymptoms } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedSymptoms } from "../../../catalogs/supported-symptoms";
import { AddButton } from "../../../components/ui-kit/add-button";
import { SymptomPlotModel } from "../../../models/plotting";
import { useFetch } from "../../../utils/use-fetch";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AddSymptomsDialog } from "./add-symptoms-dialog";
import { Symptoms, SymptomsItem } from "./symptoms";
import { SymptomsTable } from "./symptoms-table";

export const SymptomsRoute = observer(function SymptomsRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const supportedSymptoms = useSupportedSymptoms();
  const [model] = useState(
    () => new Symptoms([], api.permissions, api.requireUserUUID()),
  );

  const [data, setData] = useState<SymptomsItem[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      // The generic list rather than `hf/symptoms`, which serves only the two
      // HF-validation study symptoms: the page shows every catalog symptom the
      // patient has, including the ones they reported from the app themselves.
      const response = await apiGetSymptoms({
        query: { patient_ids: [patient.patientId] },
        meta: { useSessionCache: !isRefetch },
      });
      model.updateData(response.data.symptoms);
      setData(response.data.symptoms);
    },
    [patient.patientId],
  );

  // The whole catalog, in its own order: the plot model keeps only the names
  // that actually have enough datapoints, so a patient with two symptoms still
  // gets two series.
  const plot = usePlot(
    () =>
      new SymptomPlotModel(
        model,
        supportedSymptoms.map((s) => s.data_name),
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Symptoms`} loading={loading}>
      <PlotCanvas model={plot} />

      <SymptomsTable
        filterActions={
          <>
            <AddButton onClick={() => setShowAddDialog(true)}>
              <Trans>Add symptoms</Trans>
            </AddButton>
            <PlotControls model={plot} />
          </>
        }
        api={api}
        data={data}
        onInvalidated={refetch}
        patient={patient}
        persistState
      />

      {showAddDialog && (
        <AddSymptomsDialog
          app={props.app}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
